import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { mdiWebcam, mdiWebcamOff } from '@mdi/js';
import Icon from '@mdi/react';
import { AppStateType } from '../../constants/enums';
import { BROADCASTING_STATE } from '../../constants/myWebcam';
import { locales } from '../../locales/en';
import { openMyWebcamFloater } from '../../state/modals/state.floater';
import CountBadge from '../common/CountBadge';
import Tooltip from '../common/Tooltip';
import { useAppStateSelector } from '../Hooks/useAppState';
import { useUserSelector } from '../Hooks/useUser';
import { palette } from '../styles/colors';
import IconButton from './IconButton';
import "./ButtonUserBroadcast.linaria.module.css!=!./ButtonUserBroadcast?./ButtonUserBroadcast.linaria.module.css?css=H4sIAAAAAAAAA52Vy3KbMBSG93kKNp1JFvIAxtSYTfsMXXTJCCQjGd0iiVuYvHsnxheJprWdnfXr0/n1H0bHK2h5wWRBkdrXycpbTQoiREUNrFS7MH9/mrexB+MlXEprJXf4xuObJc/w3jo082i2pDWtiYOTsBhJEpZ7fzVxqGsqTnSshkt5WdgsGa3xV17SyMGxh+Mlfsrqnmi8E83yxDGtyzOPZ0t+TuAcIKHboMvKT3zpz3p/3i+lRlhfdyLbx/24KdJDz/ssMF09ETybrdZRvIk05hdWJ0LUWREJXPdWH+GeIktO7Iz+4BhRGEjBxsBUGmMRQIGCZw4HMNNBEoVqeJnmrBuG9qhI3xDsu78V/0ZxevR42CQSb4koXRNP8ZI85BFePdoN+85JEaVpCOP4E2kRZf01myjrtgZzz2eh+XEe8FlnVx9rDz3si9dabU8unrII8zWPDtNsbF0PT/Fz+BbPnIpzySx12qO2Ng7joiQpqaL7JO/ZJ6vNxyu7bZSaOsLQq3pLWg6Me714xwVM3bo3FH/O3GuDIoM2jVv0hrIYT/f6NLqDsXIn9g3lkzl++/vAbVyKsYiSMf4+tvdqZ6tj405x5v42BYk5P6STkoZaKsVOYwYt7fB5PqoiaqU0WTVBQTk8MqplBv+mlvwiEMk+WJsAQ4MBFUC2NqBiTwW1bon+YMVa/bvET2b/V6XsBz2Q/YSoUQyOOyoYFRiUTFZNfnpMoRry89v9+O21d6uGfP6XABoi2prdJvyWl7Bqai1bgUAlmdS7DupnAAyupEBQjy856HHZUAsez/7lbnUJIvateA0lJWgCwErJLFXgetnTNa2GwrC2wsKCWuPxJX9/+gMSZ1MW8wgAAA=="
const titles = {
  OFF: locales.userBroadcast.broadcastingOff,
  ON: locales.userBroadcast.broadcastingOn,
  ERROR: locales.userBroadcast.broadcastingError,
  NOT_AVAILABLE: locales.userBroadcast.broadcastingNotAvailable,
  UNINITIALIZED: locales.userBroadcast.uninitialized
};
const icons = {
  OFF: mdiWebcam,
  ON: mdiWebcam,
  ERROR: mdiWebcamOff,
  NOT_AVAILABLE: mdiWebcamOff,
  UNINITIALIZED: mdiWebcam
};
const colors = {
  OFF: palette.contrastIcons,
  ON: palette.secondary,
  ERROR: palette.orangeMFC,
  NOT_AVAILABLE: palette.inactiveGrey
};
const standardIconStyles = "atm_lo_idpfg4 atm_le_idpfg4 atm_lk_idpfg4 atm_ll_idpfg4 atm_h0_yh40bf";
const compactIconStyles = "atm_lo_t94yts atm_le_t94yts atm_lk_t94yts atm_ll_t94yts atm_h0_idpfg4";
const iconStyles = "atm_3f_idpfg4 atm_1tw2wy5_6jwmw9 atm_1r4nng9_1negwtr atm_l5ldfd_6zdawv atm_1nz4nb_6zdawv atm_u5l7mh_1660a22 atm_19v8sem_1660a22 atm_ttjwaw_qgp822 atm_vei9yu_qgp822 atm_1p8t202_bh6hc1 atm_16sg1ea_bh6hc1 atm_mvmna6_bh6hc1 atm_d1sd5k_bh6hc1 atm_krva2p_yh40bf atm_1a82bny_14y27yu";
const stylesRoot = "atm_mk_h2mmj6";
const stylesPulse = "atm_p_1uoos9c";
const stylesPulseSemiCompact = "atm_p_1wjtn3p";
const BroadcastDot = /*#__PURE__*/styled('div')({
  name: "BroadcastDot",
  class: "bwxrxhf",
  propsAsIs: false
});
const broadcastingTooltipStyles = "atm_v4dhtz_q0oihd";
interface IButtonUserBroadcast {
  showParticipants?: boolean;
  disableTooltip?: boolean;
  semiCompact?: boolean;
  compact?: boolean;
  compactIcon?: boolean;
  header?: boolean;
  uid: number;
}
const ButtonUserBroadcast = (props: IButtonUserBroadcast) => {
  const {
    showParticipants = true,
    disableTooltip,
    semiCompact,
    compact,
    compactIcon,
    header,
    uid
  } = props;
  const participants = useUserSelector(uid, 'participants') || [];
  const broadcasting = useAppStateSelector(AppStateType.MYWEBCAM, 'broadcasting');
  const iconColor = colors[broadcasting];
  const iconPath = icons[broadcasting];
  const tooltipTitle = titles[broadcasting];
  const isBroadcastingAvailable = broadcasting !== BROADCASTING_STATE.NOT_AVAILABLE;
  const numberOfParticipants = participants.length;
  const participantCount = isBroadcastingAvailable && showParticipants ? numberOfParticipants : 0;
  const handleClick = () => {
    if (broadcasting !== BROADCASTING_STATE.NOT_AVAILABLE) {
      openMyWebcamFloater(header);
    }
  };
  const isMemberBroadcasting = broadcasting === BROADCASTING_STATE.ON;
  if (compact) {
    if (isMemberBroadcasting) {
      return <BroadcastDot />;
    }
    return null;
  }
  const core = <div className={stylesRoot}>
            <IconButton data-testid="IconButton__UserBroadcastToggle" onClick={handleClick} className={cx(iconStyles, isMemberBroadcasting && stylesPulse, isMemberBroadcasting && semiCompact && stylesPulseSemiCompact, compactIcon ? compactIconStyles : standardIconStyles)}>
                <Icon path={iconPath} color={iconColor} />
            </IconButton>
            <CountBadge value={participantCount} size="medium" placement="rim" />
        </div>;
  if (disableTooltip) {
    return core;
  }
  return <Tooltip className={broadcastingTooltipStyles} enterDelay={1000} enterTouchDelay={1500} placement="bottom" title={tooltipTitle}>
            {core}
        </Tooltip>;
};
export default ButtonUserBroadcast;